import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

export const getUsersByLocation = createAsyncThunk(
  "LocationAccess/getUsersByLocation",
  async (payload, { rejectWithValue }) => {
    try {
      const { locationId, ...params } = payload;
      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );
      const queryParams = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredParams)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            queryParams.append(key, item);
          });
        } else {
          queryParams.append(key, value);
        }
      }
      const queryString = queryParams.toString();
      const url = queryString
        ? `${API_URL.LOCATION_ACCESS.GET_USERS_BY_LOCATION(locationId)}?${queryString}`
        : API_URL.LOCATION_ACCESS.GET_USERS_BY_LOCATION(locationId);
      const { data } = await axios.get(url);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getAvailableUsers = createAsyncThunk(
  "LocationAccess/getAvailableUsers",
  async (payload, { rejectWithValue }) => {
    try {
      const { locationId, searchText, pageNumber = 1, pageSize = 10 } = payload;
      const params = new URLSearchParams();
      if (searchText) params.append("searchText", searchText);
      params.append("pageNumber", pageNumber);
      params.append("pageSize", pageSize);

      const { data } = await axios.get(
        `${API_URL.LOCATION_ACCESS.GET_AVAILABLE_USERS(locationId)}?${params.toString()}`
      );
      return { ...data, pageNumber };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const addUsersToLocation = createAsyncThunk(
  "LocationAccess/addUsers",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.LOCATION_ACCESS.ADD_USERS, payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const removeUserFromLocation = createAsyncThunk(
  "LocationAccess/removeUser",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(API_URL.LOCATION_ACCESS.REMOVE_USER, {
        data: payload,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

const initialState = {
  users: [],
  availableUsers: [],
  availableUsersPagination: {
    current: 1,
    pageSize: 10,
    total: 0,
    hasMore: true,
  },
  pagination: {
    current: 1,
    pageSize: 10,
    total: 0,
  },
  loading: false,
  error: null,
  selectedUsers: [],
  cachedSearch: "",
};

const locationAccessSlice = createSlice({
  name: "locationAccess",
  initialState,
  reducers: {
    setSelectedUsers: (state, action) => {
      state.selectedUsers = action.payload;
    },
    clearSelectedUsers: (state) => {
      state.selectedUsers = [];
    },
    clearAvailableUsers: (state) => {
      state.availableUsers = [];
      state.availableUsersPagination = {
        current: 1,
        pageSize: 10,
        total: 0,
        hasMore: true,
      };
      state.cachedSearch = "";
    },
    resetAvailableUsersPagination: (state) => {
      state.availableUsersPagination = {
        current: 1,
        pageSize: 10,
        total: 0,
        hasMore: true,
      };
    },
    resetLocationAccessState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Get Users By Location
      .addCase(getUsersByLocation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUsersByLocation.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload?.data?.list || [];
        state.pagination = {
          current: action.payload?.data?.pageNumber || 1,
          pageSize: action.payload?.data?.pageSize || 10,
          total: action.payload?.data?.totalRecords || 0,
        };
      })
      .addCase(getUsersByLocation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Available Users
      .addCase(getAvailableUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAvailableUsers.fulfilled, (state, action) => {
        state.loading = false;
        const newUsers = action.payload?.data?.list || action.payload?.data || [];
        const pageNumber = action.payload?.pageNumber || 1;

        // If it's the first page, replace the list; otherwise append
        if (pageNumber === 1) {
          state.availableUsers = newUsers;
        } else {
          state.availableUsers = [...state.availableUsers, ...newUsers];
        }

        state.availableUsersPagination = {
          current: pageNumber,
          pageSize: action.payload?.data?.pageSize || 10,
          total: action.payload?.data?.totalRecords || newUsers.length,
          hasMore: newUsers.length === (action.payload?.data?.pageSize || 10),
        };
      })
      .addCase(getAvailableUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add Users
      .addCase(addUsersToLocation.pending, (state) => {
        state.loading = true;
      })
      .addCase(addUsersToLocation.fulfilled, (state) => {
        state.loading = false;
        state.selectedUsers = [];
      })
      .addCase(addUsersToLocation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Remove User
      .addCase(removeUserFromLocation.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeUserFromLocation.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(removeUserFromLocation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedUsers,
  clearSelectedUsers,
  clearAvailableUsers,
  resetAvailableUsersPagination,
  resetLocationAccessState,
} = locationAccessSlice.actions;

export default locationAccessSlice.reducer;
